import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import { Step } from '../data/step';
import { clientState } from '../states/auth';

interface Response {
  data: {
    success: boolean;
    data: Array<Step>;
  };
}

export function useResetAcademyProgress(
  userId: string,
  options?: UseMutationOptions<Response, AxiosError, void>
) {
  const queryClient = useQueryClient();
  const client = useRecoilValue(
    clientState({
      contentType: 'application/json',
      apiVersion: 1,
    })
  );

  return useMutation<Response, AxiosError, void>(
    () => client.delete(`/academy/user/${userId}/userSteps`),
    {
      ...options,
      onSuccess: (response, variables, context) => {
        // Update the academyLevels cache with the reset data
        const currentLevelsData = queryClient.getQueryData(['academyLevels']);

        if (currentLevelsData) {
          // Group steps by level
          const stepsByLevel: Record<number, Step[]> = {};
          response.data.data.forEach(step => {
            if (!stepsByLevel[step.levelNumber]) {
              stepsByLevel[step.levelNumber] = [];
            }
            stepsByLevel[step.levelNumber].push(step);
          });

          // Update the cache with the reset steps for each level
          queryClient.setQueryData(['academyLevels'], (oldData: any) => {
            if (!oldData) return oldData;

            const newLevelData = { ...oldData.data.data };

            // Update each level with its reset steps
            Object.keys(stepsByLevel).forEach(levelNumber => {
              newLevelData[levelNumber] = stepsByLevel[Number(levelNumber)];
            });

            // Create new data object with updated levels
            const newData = {
              ...oldData,
              data: {
                ...oldData.data,
                data: newLevelData,
              },
            };

            return newData;
          });
        }

        // Also invalidate the academyLevels query to ensure all data is refreshed
        queryClient.invalidateQueries(['academyLevels']);

        // Call the original onSuccess if it exists
        options?.onSuccess?.(response, variables, context);
      },
    }
  );
}
